import * as R from 'ramda';
import { connect } from 'react-redux';
import { lifecycle, withHandlers, withProps, withState, withStateHandlers } from 'recompose';
import { Form } from 'antd';
import moment from 'moment';

import { modalNames } from '../../modals';
import RequestForm from '../../components/request-form';

import { fetchUsers } from '../../redux/thunk/users';
import { getRequestSettings } from '../../redux/selectors';
import { handleRequestUpdateModal } from '../../redux/thunk/requests';

import {
  handleAttachmentsFetch,
  handleAttachmentsCreate,
  handleAttachmentsRemove,
} from '../../redux/thunk/attachments';

import { fetchContacts, fetchProjects } from '../../api/apiko-api';

import { filterAttachments } from '../../containers/request-attachments/utils';

import { debounceTime } from '../constants';

import {
  checkIfCurrentUserSalesAgent,
  requestSettingItemToOption,
  requestSettingToOptions,
  requestLabelsToOptions,
  rejectedFilesErrors,
  getCurrentUserId,
  getOrgSettingId,
  validateFiles,
  getItemsById,
  filterUsers,
  getItemById,
  debounce,
} from '../../utils';

const debouncedContactsFetch = debounce(fetchContacts, debounceTime);

const getRequest = (state, { requestId }) => {
  const { requests = {}, contacts = {} } = state;
  const request = getItemById(requestId, requests);
  const requestContacts = getItemsById(request.contacts, contacts);

  return {
    ...request,
    contacts: requestContacts.map(({ _id, fullName, emails }) => ({
      _id,
      fullName,
      emails,
    })),
    isLoading: requests.isUpdating,
  };
};

const mapStateToProps = ({
  users: { items },
  ...state
}, { payload }) => {
  const request = getRequest(state, payload);
  const { requestId } = payload;
  const attachments = filterAttachments(requestId)(state);
  const isCurrentUserSalesAgent = checkIfCurrentUserSalesAgent(items);

  return ({
    request,
    requestId,
    attachments,
    users: filterUsers(items),
    isCurrentUserSalesAgent,
    isFetchingRequests: request.isFetching,
    isAttachmentsLoading: state.attachments.isCreating
      || state.attachments.isRemoving
      || (!attachments.length && state.attachments.isFetching),
    ...getRequestSettings(state),
    tasks: state.tasks.items,
  });
};


export const EditRequestModal = Form.create()(R.compose(
  connect(
    mapStateToProps,
    {
      fetchUsers,
      handleRequestUpdateModal,
      onAttachmentsCreate: handleAttachmentsCreate,
      onAttachmentsFetch: handleAttachmentsFetch,
      onAttachmentRemove: handleAttachmentsRemove,
    },
  ),
  withState('projects', 'setProjects', ({ request: { project } }) => project ? [project] : []),
  withState('isFetchingProjects', 'setIsFetchingProjects', true),
  lifecycle({
    componentDidMount() {
      fetchProjects()
        .then((projects) => {
          this.props.setProjects(projects);
          this.props.setIsFetchingProjects(false);
        });

      if (!this.props.users) {
        this.props.fetchUsers();
      }
      const { requestId, onAttachmentsFetch } = this.props;

      onAttachmentsFetch({ requestId });
    },
  }),
  withProps(() => ({
    currentUserId: getCurrentUserId(),
    title: 'Edit Request',
    isEdit: true,
  })),
  withStateHandlers(
    ({
      request: { contacts },
      requestStatuses,
      requestSources,
      requestLabels,
      request,
      users,
      tasks,
    }) => ({
      selectedRequestSource: requestSettingItemToOption(requestSources[request.source]),
      selectedRequestStatus: requestSettingItemToOption(requestStatuses[request.status]),
      selectedRequestLabels: requestLabelsToOptions(request.labels.map(id => requestLabels[id])),
      subject: request.subject,
      description: request.description,
      date: moment(request.requestDate),
      requestSourcesOptions: requestSettingToOptions(requestSources),
      requestStatusesOptions: requestSettingToOptions(requestStatuses),
      requestLabelsOptions: requestSettingToOptions(requestLabels),
      selectedContacts: R.map(R.prop('_id'), R.prop('contacts', request)),
      selectedUsers: request.assignees,
      selectedProject: request.project && request.project._id,
      searchedContacts: contacts,
      isFetchingContacts: false,
      isVisible: false,
      users,
      tasks,
      requestTasks: request.tasks,
      isStatusPopUpVisible: false,
    }),
    {
      setSelectedRequestSource: ({ requestSourcesOptions }) => ({ _id }) => ({
        selectedRequestSource: R.find(R.propEq('_id', _id), requestSourcesOptions),
      }),
      setSelectedRequestStatus: ({ requestStatusesOptions }) => ({ _id }) => ({
        selectedRequestStatus: R.find(R.propEq('_id', _id), requestStatusesOptions),
      }),
      setSelectedRequestLabels: () => selectedLabels => ({
        selectedRequestLabels: selectedLabels,
      }),
      setSelectedProject: () => projectId => ({
        selectedProject: projectId,
      }),
      isFetchingContactsData: () => value => ({
        isFetchingContacts: value,
      }),
      setContactsData: () => (contacts = []) => ({
        searchedContacts: contacts,
      }),
      setSelectedContacts: () => value => ({
        selectedContacts: value,
      }),
      setSelectedUsers: () => value => ({
        selectedUsers: value,
      }),
      setIsStatusPopUpVisible: () => value => ({
        isStatusPopUpVisible: value,
      }),
    },
  ),
  withHandlers({
    handleCreateNewContact: props => () => props.openModal({ name: modalNames.ADD_NEW_CONTACT }),
    handleContactSearch: props => (value) => {
      if (value.length > 1) {
        props.isFetchingContactsData(true);
        debouncedContactsFetch({ searchTerm: value, simpleList: true })
          .then(({ data }) => {
            props.setContactsData(data.contacts);
            props.isFetchingContactsData(false);
          });
      } else props.setContactsData();
    },
    onSubmitForm: ({
      selectedRequestSource,
      selectedRequestStatus,
      selectedRequestLabels,
      selectedUsers,
      selectedProject,
      uploadedFiles,
      request,
      form,
      ...actions
    }) => (event) => {
      event.preventDefault();
      form.validateFieldsAndScroll((err, {
        contacts,
        subject,
        description,
        requestDate,
      }) => {
        if (!err) {
          const data = {
            contacts,
            subject,
            description,
            project: selectedProject || null,
            assignees: selectedUsers,
            source: selectedRequestSource._id,
            status: selectedRequestStatus._id,
            labels: selectedRequestLabels.map(({ _id }) => _id),
            requestDate: requestDate.toString(),
          };

          actions.handleRequestUpdateModal(request._id, data);
        } else {
          console.error(err);
        }
      });
    },
    openFilePicker: () => event => (
      event.target.nextElementSibling.click()
    ),
    onAttachmentsAdd: ({ requestId, onAttachmentsCreate }) => (event) => {
      const files = Array.from(event.target.files);

      const formData = new FormData();

      const { acceptedFiles, rejectedFiles } = validateFiles(files);

      if (acceptedFiles && acceptedFiles.length > 0) {
        // add files to the form data
        acceptedFiles.forEach(file => formData.append('files', file));

        // add requestId to the form data
        formData.append('requestId', requestId);

        onAttachmentsCreate(formData);
      }

      rejectedFilesErrors(rejectedFiles);

      // reset input
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
    },
    onDrop: ({ requestId, onAttachmentsCreate }) => (files, rejectedFiles) => {
      const formData = new FormData();

      if (files && files.length > 0) {
        // add files to the form data
        files.forEach(file => formData.append('files', file));

        // add requestId to the form data
        formData.append('requestId', requestId);

        onAttachmentsCreate(formData);
      }

      rejectedFilesErrors(rejectedFiles);
    },
    handleChangeStatus: ({
      requestStatusesOptions,
      setIsStatusPopUpVisible,
      setSelectedRequestStatus,
    }) => (requestStatus) => {
      const customerStatusId = getOrgSettingId(requestStatusesOptions, 'Won');

      if (requestStatus._id === customerStatusId && requestStatus.label) {
        return setIsStatusPopUpVisible(true);
      }

      setIsStatusPopUpVisible(false);

      return setSelectedRequestStatus(requestStatus);
    },
    handleChangeLabels: ({ setSelectedRequestLabels }) => (requestLabels) => {
      const labels = !R.isNil(requestLabels) ? requestLabels : [];

      return setSelectedRequestLabels(labels);
    },
    onConfirmStatusChange: ({
      requestStatusesOptions,
      setIsStatusPopUpVisible,
      setSelectedRequestStatus,
    }) => () => {
      setIsStatusPopUpVisible(false);
      return setSelectedRequestStatus({
        _id: getOrgSettingId(requestStatusesOptions, 'Won'),
      });
    },
    onCancelStatusChange: ({ setIsStatusPopUpVisible }) => () => {
      setIsStatusPopUpVisible(false);
    },
  }),
)(RequestForm));



// WEBPACK FOOTER //
// ./src/modals/edit-request/index.js